import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCurrency } from '../context/CurrencyContext';

const Terms = () => {
  const { t, selectedCountry } = useCurrency();
  const { countryCode } = useParams();
  const isIndia = selectedCountry.code === 'IN';
  const companyName = isIndia
    ? 'Wisedrive Technologies Private Limited'
    : 'Wisedrive Technologies (M) Sdn. Bhd. Limited';
  const supportEmail = isIndia ? 'support.in@wisedrive.com' : 'support.my@wisedrive.com';
  const supportPhone = isIndia ? '7353 34 35 36' : '+60 122559610';

  const definitions = [
    { term: 'Our/We and Us', definition: `means ${companyName}` },
    {
      term: 'Agreement',
      definition:
        'means the agreement between Wisedrive and the Customer pursuant to which Wisedrive agrees to procure that an Inspector carries out an Inspection in accordance with these terms and conditions.',
    },
    {
      term: 'Cancellation Fee',
      definition: 'means the amount payable by the Customer as referred in these terms and conditions.',
    },
    {
      term: 'Customer/You',
      definition: 'mean a customer of Wisedrive who places an order for an Inspection.',
    },
    {
      term: 'Fee',
      definition: 'means the amount payable by the Customer to Wisedrive for the Inspection as set out at https://www.wisedrive.com',
    },
    {
      term: 'Inspection',
      definition:
        'means an audio visual external vehicle inspection and test drive carried out by an Inspector on behalf of the Wisedrive for the Customer in accordance with these terms and conditions.',
    },
    {
      term: 'Inspector',
      definition:
        'refers to the qualified inspector who carries out the Inspection on behalf of Wisedrive. Inspectors are subject to availability and may be supplemented by the use of appropriate third party agents.',
    },
    {
      term: 'Report',
      definition:
        "means the written report produced by the Inspector as part of the Inspection, which includes the Customer's name and address, registration details of the specific Vehicle inspected and its location, whether the Vehicle has passed or failed the Inspection, a list of checks carried out and an outline of any areas of concern.",
    },
    {
      term: 'Report Checklist',
      definition:
        'means the list of areas which will be or have been checked by the Inspector as part of the Physical Inspection, which are available at https://www.wisedrive.com',
    },
    {
      term: 'Vehicle',
      definition: 'means the vehicle in respect of which the Customer requires an Inspector to carry out an Inspection.',
    },
  ];

  const generalItems = [
    'Inspections are prepared from visual and external checks of the Vehicle only and are limited to the parts and/or items identified on the Report. Inspections do not involve dismantling or disturbing any structure, assembly, component or internal mechanism. The Inspector will carry out a road test of the Vehicle and will produce a Report. There are certain parts of the Vehicle which will not be looked at. You should read the Report Checklist carefully to see what will not be checked.',
    'A copy of the Report will be sent to You by email to the email address You provide when ordering an Inspection. Due to the nature of the internet and the virus-protection software utilised by your internet service provider or loaded on to your computer, it may not be possible in all cases to deliver the Report by email. In the event that You do not receive Your Report within 24 hours of the Wisedrive Vehicle Inspection taking place you must notify Us as soon as reasonably possible.',
    'Items which are listed on the Report Checklist as a part/item to be checked, but which are not reasonably accessible on the Vehicle, will not be inspected. In such circumstances, We will be under no obligation to inspect and/or report on the part/item in question and the Inspector will mark the relevant section of the Report accordingly.',
    'The vehicle should be reasonably clean to ensure that the Inspector can clearly see and accurately report on the relevant vehicle parts inspected. Where the vehicle is not clean, the Inspector reserves the right not to comment on those parts and will mark the Report Checklist accordingly. In such case, the Report Fee shall be fully payable.',
  ];

  const exclusionItems = [
    'Any parts which require dismantling/removing/disconnecting or taking apart for inspection purposes',
    'Vehicle data checks',
    'Oil and fuel consumption',
    'Source of oil leaks',
    'Brake lining material (other than what is immediately visible and can be seen without dismantling anything)',
    'Brake fluid for contamination',
    'Operation of cassette, compact-disc players, TV/DVD/satellite navigation systems, Bluetooth or other in-car entertainment',
    'Sound or radio reception and alarm systems',
    'Cylinder compression, vehicle electrics and electronics which would require specific diagnostic equipment',
    'Exhaust emissions and/or catalytic converters',
    'The accuracy of in-car computer systems',
    'Air conditioning functions and efficiency',
    'The habitation area of Motorhomes',
    'Vehicle Technical Systems including engine-management, fuel, ignition, air-conditioning, lambda-controlled systems, stereo radio, satellite navigation, active suspensions, anti-lock braking systems, four-wheel drive, traction control, alarm and immobiliser systems',
    'Non-standard accessories as a result of personal modifications or otherwise',
  ];

  const inspectionCannotItems = [
    'a) confirm whether or not the passenger or luggage compartments are watertight',
    'b) predict the life expectancy of exhaust systems, clutches or dual-mass flywheels',
    'c) provide any indication of any faults which might have become apparent had the Vehicle been driven in excess of the speed reached or the distance travelled during any road test',
    'd) confirm that all or any of the components of the Vehicle are original or replacement components manufactured by the vehicle manufacturer',
    'e) include the inspection of any parts, components or areas of the Vehicle that are not reasonably visible or accessible to the Inspector at the time of the Inspection',
    "f) confirm authenticity of the Vehicle's Registration Document, the mileage, the Excise Licence, the VIN, the Engine Number, or the Vehicle service history",
    'g) check for any recall notices. Wisedrive recommends that You check with the RTO or manufacturer that the Vehicle has not been subject to such notices.',
  ];

  const postExclusionItems = [
    'The Inspection can only describe and/or identify defects actually found and/or which are reasonably capable of being found upon an external visual examination of the Vehicle at the time of Inspection. We cannot be held liable and are not responsible for any latent defects which are later discovered unless We can reasonably have been expected to see them during an Inspection. In particular, Vehicles over 5 years old, those that have a high mileage and/or have been subjected to abnormal use may have latent defects.',
    'Please note that used vehicles may contain serious internal, structural or mechanical defects and/or hidden corrosion, which will not be detectable from an external, visual examination. These vehicles may prove to be less reliable and may need more frequent repairs/servicing than more modern vehicles.',
    'If there is no documentary evidence of a recent engine timing belt (cam belt) replacement, We recommend that the belt of the Vehicle is checked or replaced prior to purchase.',
    "If the Vehicle does not have a recent service history or it was not available to the Inspector at the time of the Inspection, then We recommend that a service in accordance with the Vehicle manufacturer's specifications be undertaken prior to purchase.",
    'Please note that the life expectancy of an exhaust or clutch system is uncertain and difficult to predict. The fact that such items have not been identified as faulty on the Report does not imply that such systems will have a continuing life expectancy from the time of Our check.',
  ];

  const roadTestingItems = [
    'In order for our Inspectors to carry out a Road Test on the Vehicle, You must ensure that the vehicle contains sufficient: (a) Water; (b) Oil; (c) Petrol.',
    'Road testing is carried out within the immediate area of the Inspection.',
    'If the Vehicle is in an unroadworthy condition or contains insufficient oil, water and/or petrol, the Fee for the Inspection shall remain fully payable and an additional Fee will be payable for any subsequent road test.',
    "It is the Customer's responsibility to ensure that proper provision is made for the Inspection to be carried out on garage premises or on an off-road site with an adequate level, hard, dry standing area. Our Inspector needs enough room to walk round the Vehicle and fully open all doors.",
    'Good quality indoor lighting or daylight must be provided in the location where the Inspection is carried out. We are unable to check Vehicles in poor light or darkness.',
  ];

  const feesAndCancellationItems = [
    'The Fee must be paid during the time of booking the Inspection.',
    'We reserve the right to refuse to carry out an Inspection or to issue a Report on any vehicle that: the Inspector cannot reasonably access; or appears to be not of standard production, modified, un-roadworthy or unsafe to drive.',
    'Where the Customer has provided incorrect or incomplete information that has prevented Us from carrying out an Inspection, We are entitled to treat the Agreement as terminated and to retain the Fee in full.',
    `You have the right to cancel an Inspection and receive a full refund of the Fee, 24hrs before the inspection is scheduled. Once the inspection is carried out no refund will be issued.`,
    `To cancel an Inspection, You must inform Our Service Support Team by emailing ${supportEmail} or by calling ${supportPhone}, Monday to Friday 9am to 5pm.`,
  ];

  const complaintItems = [
    `If You have a complaint or are dissatisfied with any Inspection carried out, please contact Us on ${supportPhone} as soon as possible. We may need to re-inspect the Vehicle to deal with the complaint and You agree to allow Us to do so.`,
    'No repairs should be effected or any parts replaced on the Vehicle which is the subject of a complaint before the Vehicle has been re-inspected by Us.',
    'We reserve the right to require another of Our Inspectors to re-inspect any Vehicle at Our own expense should it be considered necessary to deal with any complaint.',
  ];

  const outsideControlItems = [
    'We will not be liable for delay to/non-performance of the Inspection due to events or circumstances outside Our reasonable control. We will take steps to minimise the effects of this where possible.',
    'Wisedrive will only be liable to the Customer to the extent that Wisedrive fails to carry out the Inspection in accordance with these terms and conditions. Without limiting the foregoing, neither Wisedrive nor the Inspector will be liable for Losses that are not a reasonably foreseeable result of any breach, Losses not caused directly by Our breach, business losses, or Losses that increase as a result of a breach by You of these terms.',
    'Nothing herein will affect the statutory rights of a consumer under any applicable statute.',
  ];

  const enforcementItems = [
    'Each of the clauses of these terms operates separately. If any court of relevant authority decides that any of them are unlawful, the remaining clauses will remain in full force and effect.',
    'The Agreement is between You and Wisedrive. No other person shall have any rights to enforce any of its terms.',
  ];

  const reportAdviceItems = [
    "You are strongly advised to discuss the findings within the Report with the seller prior to making any decision to purchase the Vehicle. If You have not viewed the Vehicle personally, You must ensure that the Vehicle's cosmetic appearance and specification is satisfactory and suitable for your needs and expectations.",
  ];

  const Section = ({ title, children }) => (
    <div className="mb-10 pb-10 border-b border-slate-100 last:border-0 last:mb-0 last:pb-0">
      <h2 className="text-xl font-semibold text-slate-900 mb-4">{title}</h2>
      {children}
    </div>
  );

  const BulletList = ({ items }) => (
    <ul className="space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 text-slate-600 leading-relaxed">
          <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-teal-500" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar forceWhite hasBanner={false} />

      {/* Hero */}
      <div className="bg-slate-900 pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={`/${countryCode}`}
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('legal.backToHome')}
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-teal-500/20 p-3 rounded-xl">
              <FileText className="h-6 w-6 text-teal-400" />
            </div>
            <span className="text-teal-400 text-sm font-medium uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{t('legal.terms.title')}</h1>
          <p className="text-slate-400 text-lg">{t('legal.terms.subtitle')}</p>
          <p className="text-slate-500 text-sm mt-4">{t('legal.effectiveDate')}: January 1, 2024</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-8 md:p-12">

            {/* Definitions */}
            <Section title="Definitions">
              <div className="space-y-4">
                {definitions.map((def, i) => (
                  <div key={i} className="text-slate-600 leading-relaxed">
                    <strong className="text-slate-800">{def.term}</strong> {def.definition}
                  </div>
                ))}
                <p className="text-slate-600 leading-relaxed mt-4">
                  Wisedrive provides a number of different categories of Inspection. Details are available at{' '}
                  <a href="https://www.wisedrive.com" className="text-teal-600 hover:underline">
                    https://www.wisedrive.com
                  </a>{' '}
                  or by telephoning {supportPhone}.
                </p>
              </div>
            </Section>

            {/* General */}
            <Section title={t('legal.terms.general')}>
              <BulletList items={generalItems} />
            </Section>

            {/* Exclusions */}
            <Section title={t('legal.terms.exclusions')}>
              <p className="text-slate-600 mb-4">{t('legal.terms.exclusionsSubtitle')}</p>
              <BulletList items={exclusionItems} />
              <p className="text-slate-600 mt-6 mb-4 font-medium">{t('legal.terms.inspectionCannot')}</p>
              <ul className="space-y-2">
                {inspectionCannotItems.map((item, i) => (
                  <li key={i} className="text-slate-600 leading-relaxed pl-4">{item}</li>
                ))}
              </ul>
              <div className="mt-6 space-y-4">
                {postExclusionItems.map((item, i) => (
                  <p key={i} className="text-slate-600 leading-relaxed">{item}</p>
                ))}
              </div>
            </Section>

            {/* Road Testing */}
            <Section title={t('legal.terms.roadTesting')}>
              <BulletList items={roadTestingItems} />
            </Section>

            {/* Fees and Cancellations */}
            <Section title={t('legal.terms.feesAndCancellations')}>
              <BulletList items={feesAndCancellationItems} />
            </Section>

            {/* Complaints */}
            <Section title={t('legal.terms.complaints')}>
              <BulletList items={complaintItems} />
            </Section>

            {/* Outside Control */}
            <Section title={t('legal.terms.outsideControl')}>
              <BulletList items={outsideControlItems} />
            </Section>

            {/* Enforcement */}
            <Section title={t('legal.terms.enforcement')}>
              <BulletList items={enforcementItems} />
            </Section>

            {/* Report Advice */}
            <Section title={t('legal.terms.reportAdvice')}>
              <BulletList items={reportAdviceItems} />
            </Section>

          </div>
        </div>

        {/* Contact Card */}
        <div className="mt-8 bg-teal-50 border border-teal-100 rounded-2xl p-8 text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('legal.contactUs')}</h3>
          <p className="text-slate-600 mb-4">
            Questions about our Terms of Service? We're here to help.
          </p>
          <a
            href={`mailto:${supportEmail}`}
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-teal-700 transition-colors"
          >
            {supportEmail}
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;
